/**
 *
 * @description: 该指令用于给元素添加闪烁效果
 * v-flash 基础效果
 * v-flash="false" 或者 v-flash="{ hide: true }" 不展示效果
 * v-flash="{border:true}" 同时展示边框闪烁效果
 * v-flash="{boom:true}" 呼吸效果
 * v-flash="{shineLight:false}" 不展示扫光效果
 * v-flash="{time:3000}" 设置动画时长,单位毫秒，例如：3000就表示动画运行3秒后停止
 */
const flash = {
  bind(el, binding) {
    const bindValue = flash.getBindValue(binding);
    if (bindValue === false || bindValue.hide) {
      return;
    }
    flash.createStyle(el, bindValue);
    flash.timeoutAutoDestroy(el, bindValue);
    flash.handleClassName(el, bindValue);
  },
  // 更新的时候，重新执行动画绑定逻辑
  update(el, binding) {
    // 如果动画超时了，则不进行更新
    if (el.timeOut) {
      return;
    }
    if (JSON.stringify(binding.value) === JSON.stringify(binding.oldValue)) {
      return;
    }
    const bindValue = flash.getBindValue(binding);
    // 更新前先销毁之前的动画，防止样式残留
    flash.destroyAni(el);
    if (bindValue === false || bindValue.hide) {
      return;
    }
    flash.createStyle(el, bindValue);
    flash.timeoutAutoDestroy(el, bindValue);
    flash.handleClassName(el, bindValue);
  },
  timeoutAutoDestroy(el, bindValue) {
    const element = el;
    const { time } = bindValue;
    if (time) {
      element.timer = setTimeout(() => {
        flash.destroyAni(element);
        // 设置的动画超时时间到了之后，设置一个标志位，用于标识动画超时了
        element.timeOut = true;
      }, time);
    }
  },
  handleClassName(el, bindValue) {
    const element = el;
    const { shineLight = true, border, boom } = bindValue;
    const { shineLightClass, highlightBorderClass, boomClass } = element;
    if (shineLight) {
      element.classList.add(shineLightClass);
    } else {
      element.classList.remove(shineLightClass);
    }
    if (border) {
      element.classList.add(highlightBorderClass);
    } else {
      element.classList.remove(highlightBorderClass);
    }
    if (boom) {
      element.classList.add(boomClass);
    } else {
      element.classList.remove(boomClass);
    }
  },
  getBindValue(binding) {
    const bindValue = binding.value;
    if (typeof bindValue === 'boolean') {
      return bindValue;
    }

    // 非object类型直接返回{}
    if (Object.prototype.toString.call(bindValue) === '[object Object]') {
      return bindValue;
    }
    return {};
  },
  getUUid() {
    const uuid = Math.random().toString(36).slice(2);
    return uuid;
  },
  createStyle(el, bindValue) {
    const element = el;
    const {
      speed = 1.5,
      delay = 0,
      scale = 1.1,
    } = bindValue;
    const shineLightClass = `shine-light-${flash.getUUid()}`;
    const highlightBorderClass = `highlight-border-${flash.getUUid()}`;
    const boomClass = `boom-${flash.getUUid()}`;
    element.shineLightClass = shineLightClass;
    element.highlightBorderClass = highlightBorderClass;
    element.boomClass = boomClass;
    const style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = `
      .${shineLightClass} {
        position: relative;
        overflow: hidden;
      }

      .${shineLightClass}::before {
        content: '';
        z-index: 2;
        position: absolute;
        display: inline-block;
        left: -100%;
        top: 0;
        width: 1rem;
        height: 100%;
        opacity: 0.5;
        transform: skewX(150deg);
        -webkit-transform: skewX(150deg); /* 兼容性处理 */
        background: -webkit-linear-gradient(90deg, rgba(255,255,255,0), #fff 50%, rgba(255,255,255,0)); /* 兼容性处理 */
        background: linear-gradient(90deg, rgba(255,255,255,0), #fff 50%, rgba(255,255,255,0));
        animation: move-light-${boomClass}  ${speed}s infinite ${delay}s;
        -webkit-animation: move-light-${boomClass}  ${speed}s infinite ${delay}s; /* 兼容性处理 */
      }
      
      .${boomClass}{
        animation: boom-${boomClass} 1.5s infinite;
        -webkit-animation: boom-${boomClass} 1.5s infinite; /* 兼容性处理 */
        transform: scale(${scale});
        -webkit-transform: scale(${scale}); /* 兼容性处理 */
      }

      @-webkit-keyframes move-light-${boomClass} { /* 兼容性处理 */
        100% {
          left: 125%;
        }
      }

      @keyframes move-light-${boomClass} {
        100% {
          left: 125%;
        }
      }

      .${highlightBorderClass} {
        animation: blink-border-${highlightBorderClass} ${speed}s infinite;
        -webkit-animation: blink-border-${highlightBorderClass} ${speed}s infinite; /* 兼容性处理 */
        border: 0.015rem solid #fa4614;
        border-radius: 0.16rem;
        -webkit-border-radius: 0.16rem; /* 兼容性处理 */
      }

      @-webkit-keyframes blink-border-${highlightBorderClass} { /* 兼容性处理 */
        0%,
        100% {
          border-color: rgb(250, 70, 20);
        }

        50% {
          border-color: rgba(250, 70, 20, 0.3);
        }
      }

      @keyframes blink-border-${highlightBorderClass} {
        0%,
        100% {
          border-color: rgb(250, 70, 20);
        }

        50% {
          border-color: rgba(250, 70, 20, 0.3); 
        }
      }
      @keyframes boom-${boomClass} {
        0%,
        100% {
          transform: scale(${scale});
        }

        50% {
          transform: scale(1);
        }
      }
      @-webkit-keyframes boom-${boomClass} {
        0%,
        100% {
          transform: scale(${scale});
        }

        50% {
          transform: scale(1);
        }
      }
    `;
    document.head.appendChild(style);
    element._appendStyle = style;
  },
  destroyAni(el) {
    const element = el;
    const style = element._appendStyle;
    const {
      shineLightClass,
      highlightBorderClass,
      boomClass,
      timer,
    } = element;
    if (timer) {
      clearTimeout(timer);
    }
    if (style) {
      document.head.removeChild(style);
    }
    element.classList.remove(shineLightClass);
    element.classList.remove(highlightBorderClass);
    element.classList.remove(boomClass);
    element._appendStyle = null;
  },
  unbind(el) {
    flash.destroyAni(el);
  },
};

export default flash;
